import {
  lookingForFromPublic,
  type PublicOrganizationProfile,
  type PublicOrgArticle,
  type PublicOrgAward,
  type PublicOrgPerformance,
} from "@/lib/publicOrganizationProfile";
import type { StackCategory, StackTool, ToolAccess } from "@/lib/toolStack";
import { compact, nonBlank } from "@/lib/orgStory/compact";
import { storySections } from "@/lib/orgStory/sections";
import type { OrgStoryBase, OrgStoryView } from "@/lib/orgStory/types";
import { publicWorkModelView } from "@/lib/workModel";
import { cultureTags } from "@/lib/orgProfileOptions";

/**
 * The anonymous RPC's payload → OrgStoryView (spec 2026-09-24 §6.2).
 *
 * THE ALLOWLIST BOUNDARY. Every value here comes from a key
 * get_public_organization_profile emits. Nothing reads `profile_data`,
 * `calendly_url`, reviews or team data — the RPC does not carry them, and a
 * stray key on the input is ignored because only named fields are read.
 * fromPublicProfile.test.ts proves that with a polluted input.
 *
 * The snake_case payload is mapped back into the editor's camelCase keys so
 * the public page renders through the same section components as the
 * signed-in page — the move lookingForFromPublic already makes.
 *
 * `technology_tool_groups` (else `technology_tools`, see publicToolGroups) is
 * mapped back to the editor's StackCategory shape one
 * named field at a time (the RPC emits exactly seven keys per tool, so no
 * logo, price or builder can arrive). `volume_growth_yoy` is NOT mapped:
 * the RPC drops volumeGrowthSign, so a decline would read as growth (a
 * recorded public-RPC follow-up).
 */

const performanceFromPublic = (perf: PublicOrgPerformance | null | undefined): Record<string, unknown> | null => {
  if (!perf) return null;
  const out = compact({
    reportingYear: perf.reporting_year,
    productionMetrics: compact({
      totalVolume: perf.production?.total_volume,
      totalTransactions: perf.production?.total_transactions,
      avgSalePrice: perf.production?.avg_sale_price,
    }),
    agentPerformance: compact({
      retentionRate: perf.agents?.retention_rate,
      avgTenure: perf.agents?.avg_tenure,
      newAgentSuccessRate: perf.agents?.new_agent_success_rate,
      incomeGoalHitRate: perf.agents?.income_goal_hit_rate,
    }),
    rankings: (perf.rankings ?? []).map((r) =>
      compact({ rank: r.rank, category: r.category, region: r.region, source: r.source }),
    ),
    marketPresence: compact({
      marketShare: perf.market_presence?.market_share,
      daysOnMarket: perf.market_presence?.days_on_market,
      listToSaleRatio: perf.market_presence?.list_to_sale_ratio,
      specializations: perf.market_presence?.specializations,
    }),
  });
  return Object.keys(out).length > 0 ? out : null;
};

/**
 * Field by field, so a stray key on a tool (a slug, domain or builder) cannot
 * ride along; flags and optional fields are written only when set.
 */
const toolStackFromPublic = (buckets: unknown): StackCategory[] => {
  if (!Array.isArray(buckets)) return [];
  const out: StackCategory[] = [];
  for (const b of buckets) {
    if (!b || typeof b !== "object") continue;
    const bucket = b as { category?: unknown; tools?: unknown };
    if (!Array.isArray(bucket.tools)) continue;
    const tools: StackTool[] = [];
    for (const t of bucket.tools) {
      if (!t || typeof t !== "object") continue;
      const r = t as Record<string, unknown>;
      if (typeof r.name !== "string" || r.name.trim() === "") continue;
      const tool: StackTool = { name: r.name };
      if (typeof r.category_slug === "string") tool.categorySlug = r.category_slug;
      if (r.in_house === true) tool.inHouse = true;
      if (r.driver === true) tool.driver = true;
      if (r.trained === true) tool.trained = true;
      // access and note are re-validated downstream by normalizeToolStack.
      if (typeof r.access === "string") tool.access = r.access as ToolAccess;
      if (typeof r.note === "string") tool.note = r.note;
      tools.push(tool);
    }
    if (tools.length > 0) out.push({ category: typeof bucket.category === "string" ? bucket.category : "", tools });
  }
  return out;
};

/**
 * The grouping from technology_tool_groups (migration 20261016230000); else
 * whatever technology_tools carries: the grouping on an environment with only
 * 20261016170000. Bare names (string[] technology_tools, from an environment
 * with neither) carry no category and are skipped by toolStackFromPublic, so
 * that page shows no tools rather than unvetted names; with 20261016230000 the
 * grouping key is always present.
 */
const publicToolGroups = (p: PublicOrganizationProfile): unknown =>
  Array.isArray(p.technology_tool_groups) ? p.technology_tool_groups : p.technology_tools;

const officeLabelsFromPublic = (p: PublicOrganizationProfile): string[] => {
  const fromOffices = (p.offices ?? [])
    .filter((o) => typeof o?.city === "string" && o.city.trim() !== "")
    .map((o) => (o.state ? `${o.city}, ${o.state}` : o.city));
  if (fromOffices.length > 0) return fromOffices;
  return (p.office_locations ?? []).filter((l) => typeof l === "string" && l.trim() !== "");
};

/**
 * Field-by-field allowlists for the RPC's newsroom/awards arrays, so a stray
 * nested key on either shape (e.g. from a polluted or future-widened RPC
 * response) cannot ride along into the view. Named fields only — never a
 * spread or a passthrough of the raw object.
 */
const articleFromPublic = (a: PublicOrgArticle): PublicOrgArticle => ({
  id: a.id,
  type: a.type,
  title: a.title,
  body: a.body,
  image_url: a.image_url,
  link: a.link,
  featured: a.featured,
  published_at: a.published_at,
  created_at: a.created_at,
  updated_at: a.updated_at,
});

const awardFromPublic = (a: PublicOrgAward): PublicOrgAward => ({
  id: a.id,
  name: a.name,
  org: a.org,
  year: a.year,
});

export function fromPublicProfile(p: PublicOrganizationProfile): OrgStoryView {
  const s = p.story;
  const v = p.value;

  const profileData = compact({
    missionStatement: s?.mission,
    ourStory: s?.our_story,
    whatMakesUsDifferent: s?.what_makes_us_different,
    featuredQuote: s?.quote_text ? { text: s.quote_text, attribution: s.quote_attribution ?? "" } : null,
    byTheNumbers: (p.by_the_numbers ?? []).map((b) => ({ label: b.label, value: b.value, context: b.context ?? "" })),
    brandVideoUrl: p.brand_video_url,
    galleryPhotos: (p.gallery_photos ?? []).map((url) => ({ url })),
    compensationModels: v?.compensation_models,
    primaryCompensationModel: v?.primary_compensation_model,
    feeTypes: v?.fee_types,
    noRecurringFees: v?.no_recurring_fees,
    providesLeads: v?.provides_leads,
    leadSources: v?.lead_sources,
    leadVolume: v?.lead_volume,
    trainingFrequency: v?.training_frequency?.length ? v.training_frequency.join(", ") : null,
    oneOnOneCoaching: v?.one_on_one_coaching,
    coachingFrequency: v?.coaching_frequency,
    hasTrainingLibrary: v?.has_training_library,
    trainingPrograms: (v?.training_programs ?? []).map((name) => ({ name })),
    onboardingStages: v?.onboarding_stages,
    careerStages: (v?.career_stages ?? []).map((title) => ({ title })),
    hasTransactionCoordinator: v?.has_transaction_coordinator,
    tcDetails: v?.tc_details,
    adminAssistance: v?.admin_assistance,
    technologyTools: toolStackFromPublic(publicToolGroups(p)),
    marketingSupport: v?.marketing_support,
    selectedBenefits: v?.selected_benefits,
    hasHealthInsurance: v?.has_health_insurance,
    hasRetirementPlan: v?.has_retirement_plan,
    partnershipOpportunities: v?.partnership_opportunities,
    partnershipTypes: v?.partnership_types,
    performanceData: performanceFromPublic(p.performance),
    lookingFor: lookingForFromPublic(p.looking_for),
    officesDisplay: p.offices_display === "states" ? "states" : null,
  });

  const agentCount = typeof p.agent_count === "number" && p.agent_count > 0 ? p.agent_count : null;

  const base: OrgStoryBase = {
    audience: "public",
    identity: {
      orgId: p.id,
      slug: p.slug ?? null,
      name: p.name,
      type: p.type ?? null,
      logoUrl: p.logo_url ?? null,
      coverUrl: nonBlank(p.cover_url),
      tagline: nonBlank(p.tagline) ?? nonBlank(p.description),
      city: nonBlank(p.city),
      state: nonBlank(p.state),
      foundedYear: p.founded_year ?? null,
      website: nonBlank(p.website),
      isHiring: p.is_hiring === true,
    },
    profileData,
    serviceTypes: cultureTags(p.service_types),
    officeLabels: officeLabelsFromPublic(p),
    workModel: publicWorkModelView(p.work_model),
    agentCount,
    agentCountVerified: false,
    news: { articles: (p.content ?? []).map(articleFromPublic), awards: (p.awards ?? []).map(awardFromPublic) },
    calendlyUrl: null,
  };
  return { ...base, sections: storySections(base) };
}
